import { useEffect, useRef, useCallback, useState } from 'react'
import { 
  createChart, 
  IChartApi, 
  ISeriesApi,
  CandlestickData,
  LineData,
  HistogramData,
  CrosshairMode,
  ColorType,
  LineStyle,
} from 'lightweight-charts'
import type { ChartType, Timeframe, IndicatorConfig, DrawingTool, OHLCVData } from '../../types'
import { calculateIndicator } from '../../lib/indicators'
import { useWebSocket } from '../../hooks/useWebSocket'
import { generateMockData } from '../../lib/mockData'
import { fetchBinanceKlines, isBinanceSymbol } from '../../lib/binanceApi'
import { ChartLegend } from './ChartLegend'
import { DrawingLayer } from './DrawingLayer'

interface ChartProps {
  symbol: string
  chartType: ChartType
  timeframe: Timeframe
  indicators: IndicatorConfig[]
  activeDrawingTool: DrawingTool
}

export function Chart({ 
  symbol, 
  chartType, 
  timeframe, 
  indicators,
  activeDrawingTool,
}: ChartProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const mainSeriesRef = useRef<ISeriesApi<any> | null>(null)
  const indicatorSeriesRef = useRef<Map<string, ISeriesApi<any>>>(new Map())
  const indicatorPanesRef = useRef<Map<string, ISeriesApi<any>>>(new Map())
  
  const [crosshairData, setCrosshairData] = useState<{
    time: number
    price: number
    ohlcv?: OHLCVData
  } | null>(null)
  
  const [chartData, setChartData] = useState<OHLCVData[]>([])
  
  // WebSocket for real-time updates
  const { lastPrice, isConnected } = useWebSocket(symbol)

  // Initialize chart
  useEffect(() => {
    if (!containerRef.current) return

    const chart = createChart(containerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: '#0a0a0f' },
        textColor: '#9ca3af',
      },
      grid: {
        vertLines: { color: '#1e1e2e' },
        horzLines: { color: '#1e1e2e' },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          width: 1,
          color: '#00d4ff40',
          style: LineStyle.Solid,
          labelBackgroundColor: '#00d4ff',
        },
        horzLine: {
          width: 1,
          color: '#00d4ff40',
          style: LineStyle.Solid,
          labelBackgroundColor: '#00d4ff',
        },
      },
      rightPriceScale: {
        borderColor: '#1e1e2e',
        scaleMargins: { top: 0.1, bottom: 0.2 },
      },
      timeScale: {
        borderColor: '#1e1e2e',
        timeVisible: true,
        secondsVisible: false,
      },
      handleScroll: { vertTouchDrag: true },
      handleScale: { axisPressedMouseMove: true },
    })

    chartRef.current = chart

    // Handle resize
    const handleResize = () => {
      if (containerRef.current && chartRef.current) {
        chartRef.current.applyOptions({ 
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
        })
      }
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    // Crosshair subscription
    chart.subscribeCrosshairMove((param) => {
      if (!param.time || !param.point) {
        setCrosshairData(null)
        return
      }
      
      const price = mainSeriesRef.current?.coordinateToPrice(param.point.y)
      const ohlcv = chartData.find(d => d.time === param.time)
      
      setCrosshairData({
        time: param.time as number,
        price: price || 0,
        ohlcv,
      })
    })

    return () => {
      window.removeEventListener('resize', handleResize)
      chart.remove()
      chartRef.current = null
    }
  }, [])

  // Create main series based on chart type
  const createMainSeries = useCallback(() => {
    if (!chartRef.current) return

    // Remove existing series
    if (mainSeriesRef.current) {
      chartRef.current.removeSeries(mainSeriesRef.current)
    }

    switch (chartType) {
      case 'candlestick':
      case 'heikinAshi':
      case 'hollow':
        mainSeriesRef.current = chartRef.current.addCandlestickSeries({
          upColor: '#00ff88',
          downColor: '#ff3366',
          borderUpColor: '#00ff88',
          borderDownColor: '#ff3366',
          wickUpColor: '#00ff88',
          wickDownColor: '#ff3366',
        })
        break
      case 'line':
        mainSeriesRef.current = chartRef.current.addLineSeries({
          color: '#00d4ff',
          lineWidth: 2,
        })
        break
      case 'area':
        mainSeriesRef.current = chartRef.current.addAreaSeries({
          topColor: '#00d4ff40',
          bottomColor: '#00d4ff05',
          lineColor: '#00d4ff',
          lineWidth: 2,
        })
        break
      case 'bars':
        mainSeriesRef.current = chartRef.current.addBarSeries({
          upColor: '#00ff88',
          downColor: '#ff3366',
        })
        break
    }
  }, [chartType])

  // Load and set data
  useEffect(() => {
    createMainSeries()
    
    async function loadData() {
      let data;
      
      // Use real Binance data for crypto, mock data for forex/stocks
      if (isBinanceSymbol(symbol)) {
        try {
          data = await fetchBinanceKlines(symbol, timeframe, 500)
        } catch (error) {
          console.error('Failed to fetch Binance data, using mock:', error)
          data = generateMockData(symbol, timeframe, 500)
        }
      } else {
        // Forex/Stocks use mock data (would need different API)
        data = generateMockData(symbol, timeframe, 500)
      }
      
      setChartData(data)
      
      if (mainSeriesRef.current) {
        const formattedData = formatDataForChartType(data, chartType)
        mainSeriesRef.current.setData(formattedData)
        chartRef.current?.timeScale().fitContent()
      }
    }
    
    loadData()
  }, [symbol, timeframe, chartType, createMainSeries])

  // Update indicators
  useEffect(() => {
    if (!chartRef.current || chartData.length === 0) return

    // Clear old indicator series
    indicatorSeriesRef.current.forEach((series) => {
      try {
        chartRef.current?.removeSeries(series)
      } catch (e) {
        // Series already removed
      }
    })
    indicatorSeriesRef.current.clear()
    indicatorPanesRef.current.clear()

    // Add new indicators
    indicators.forEach((indicator) => {
      if (!indicator.visible) return

      const result = calculateIndicator(indicator, chartData)
      if (!result) return

      if (indicator.overlay) {
        // Overlay on main chart
        addOverlayIndicator(indicator, result)
      } else {
        // Separate pane
        addPaneIndicator(indicator, result)
      }
    })
  }, [indicators, chartData])

  const addOverlayIndicator = (config: IndicatorConfig, data: any[]) => {
    if (!chartRef.current) return

    // Handle different indicator types
    if (['SMA', 'EMA', 'WMA', 'DEMA', 'TEMA', 'HMA', 'VWMA'].includes(config.type)) {
      const series = chartRef.current.addLineSeries({
        color: config.color,
        lineWidth: 2,
        priceLineVisible: false,
        lastValueVisible: false,
      })
      series.setData(data)
      indicatorSeriesRef.current.set(config.id, series)
    } else if (config.type === 'BB') {
      // Bollinger Bands - 3 lines
      const upperSeries = chartRef.current.addLineSeries({
        color: config.color,
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false,
      })
      const middleSeries = chartRef.current.addLineSeries({
        color: config.color,
        lineWidth: 1,
        lineStyle: LineStyle.Dashed,
        priceLineVisible: false,
        lastValueVisible: false,
      })
      const lowerSeries = chartRef.current.addLineSeries({
        color: config.color,
        lineWidth: 1,
        priceLineVisible: false,
        lastValueVisible: false,
      })
      
      upperSeries.setData(data.map(d => ({ time: d.time, value: d.upper })))
      middleSeries.setData(data.map(d => ({ time: d.time, value: d.middle })))
      lowerSeries.setData(data.map(d => ({ time: d.time, value: d.lower })))
      
      indicatorSeriesRef.current.set(`${config.id}_upper`, upperSeries)
      indicatorSeriesRef.current.set(`${config.id}_middle`, middleSeries)
      indicatorSeriesRef.current.set(`${config.id}_lower`, lowerSeries)
    } else if (config.type === 'PSAR') {
      const series = chartRef.current.addLineSeries({
        color: config.color,
        lineWidth: 0,
        pointMarkersVisible: true,
        pointMarkersRadius: 2,
        priceLineVisible: false,
        lastValueVisible: false,
      })
      series.setData(data)
      indicatorSeriesRef.current.set(config.id, series)
    }
  }

  const addPaneIndicator = (config: IndicatorConfig, data: any[]) => {
    if (!chartRef.current) return

    if (['RSI', 'CCI', 'MFI', 'WILLR', 'ROC', 'MOM'].includes(config.type)) {
      const series = chartRef.current.addLineSeries({
        color: config.color,
        lineWidth: 2,
        priceScaleId: config.id,
        pane: 1,
      })
      series.setData(data)
      indicatorPanesRef.current.set(config.id, series)
    } else if (config.type === 'MACD') {
      // MACD Line
      const macdLine = chartRef.current.addLineSeries({
        color: '#00d4ff',
        lineWidth: 2,
        priceScaleId: config.id,
        pane: 1,
      })
      // Signal Line  
      const signalLine = chartRef.current.addLineSeries({
        color: '#ff6b6b',
        lineWidth: 2,
        priceScaleId: config.id,
        pane: 1,
      })
      // Histogram
      const histogram = chartRef.current.addHistogramSeries({
        priceScaleId: config.id,
        pane: 1,
      })
      
      macdLine.setData(data.map(d => ({ time: d.time, value: d.macd })))
      signalLine.setData(data.map(d => ({ time: d.time, value: d.signal })))
      histogram.setData(data.map(d => ({ 
        time: d.time, 
        value: d.histogram,
        color: d.histogram >= 0 ? '#00ff8880' : '#ff336680'
      })))
      
      indicatorPanesRef.current.set(`${config.id}_macd`, macdLine)
      indicatorPanesRef.current.set(`${config.id}_signal`, signalLine)
      indicatorPanesRef.current.set(`${config.id}_hist`, histogram)
    } else if (config.type === 'VOL') {
      const series = chartRef.current.addHistogramSeries({
        priceScaleId: 'volume',
        priceFormat: { type: 'volume' },
        pane: 1,
      })
      series.setData(data.map((d, i) => ({
        time: d.time,
        value: d.value,
        color: chartData[i]?.close >= chartData[i]?.open ? '#00ff8840' : '#ff336640'
      })))
      indicatorPanesRef.current.set(config.id, series)
    }
  }

  // Real-time updates
  useEffect(() => {
    if (lastPrice && mainSeriesRef.current && chartData.length > 0) {
      const lastBar = chartData[chartData.length - 1]
      const updatedBar = {
        ...lastBar,
        close: lastPrice,
        high: Math.max(lastBar.high, lastPrice),
        low: Math.min(lastBar.low, lastPrice),
      }
      
      const formatted = formatDataForChartType([updatedBar], chartType)[0]
      mainSeriesRef.current.update(formatted)
    }
  }, [lastPrice, chartType, chartData])

  return (
    <div className="relative w-full h-full">
      <div 
        ref={containerRef} 
        className={`w-full h-full ${activeDrawingTool !== 'cursor' ? 'cursor-crosshair' : ''}`}
      />
      
      {/* Legend */}
      <ChartLegend 
        symbol={symbol}
        crosshairData={crosshairData}
        indicators={indicators}
      />
      
      {/* Drawing Layer */}
      <DrawingLayer 
        chart={chartRef.current}
        mainSeries={mainSeriesRef.current}
        activeTool={activeDrawingTool}
      />
      
      {/* Connection Status */}
      <div className="absolute bottom-4 left-4 flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-kit-green' : 'bg-kit-red'}`} />
        <span className="text-xs text-gray-500">
          {isConnected ? 'Live' : 'Disconnected'}
        </span>
      </div>
    </div>
  )
}

function formatDataForChartType(data: OHLCVData[], chartType: ChartType): any[] {
  switch (chartType) {
    case 'candlestick':
    case 'bars':
      return data.map(d => ({
        time: d.time,
        open: d.open,
        high: d.high,
        low: d.low,
        close: d.close,
      }))
    case 'heikinAshi':
      return calculateHeikinAshi(data)
    case 'hollow':
      return data.map(d => ({
        time: d.time,
        open: d.open,
        high: d.high,
        low: d.low,
        close: d.close,
      }))
    case 'line':
    case 'area':
      return data.map(d => ({
        time: d.time,
        value: d.close,
      }))
    default:
      return data
  }
}

function calculateHeikinAshi(data: OHLCVData[]): CandlestickData[] {
  const result: CandlestickData[] = []
  
  for (let i = 0; i < data.length; i++) {
    const current = data[i]
    const prevHA = result[i - 1]
    
    const haClose = (current.open + current.high + current.low + current.close) / 4
    const haOpen = prevHA ? (prevHA.open + prevHA.close) / 2 : (current.open + current.close) / 2
    const haHigh = Math.max(current.high, haOpen, haClose)
    const haLow = Math.min(current.low, haOpen, haClose)
    
    result.push({
      time: current.time as any,
      open: haOpen,
      high: haHigh,
      low: haLow,
      close: haClose,
    })
  }
  
  return result
}
